import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, GripVertical, Minus, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMissedCallsCount } from "@/hooks/useMissedCallsCount";
import DialerTab from "@/components/dialer/DialerTab";
import MiniCallDock from "@/components/dialer/MiniCallDock";
import SettingsTab from "@/components/dialer/SettingsTab";
import { useCallDialog } from "@/context/CallDialogContext";
import { useDismissOnOutsideClick } from "@/hooks/useDismissOnOutsideClick";

interface DraggableCallDialogProps {
  onClose: () => void;
}

interface Position {
  x: number;
  y: number;
}

const DEFAULT_WIDTH = 344;
const DOCK_MARGIN = 16;
const MINIMIZE_MS = 200;

export default function DraggableCallDialog({ onClose }: DraggableCallDialogProps) {
  const {
    dialogPosition,
    setDialogPosition,
    triggerPosition,
    activeCall,
    incomingCall,
    incomingCallEvent,
    heldCall,
    registerDialerTabSwitch,
    registerCloseHandler,
    openRequestId,
  } = useCallDialog();

  const navigate = useNavigate();
  const missedCalls = useMissedCallsCount();
  const [mainTab, setMainTab] = useState<string>("dialer");
  const [isMinimized, setIsMinimized] = useState(false);
  const isCallSurfaceActive = Boolean(activeCall || incomingCall);

  const [position, setPosition] = useState<Position>(() => {
    const saved = localStorage.getItem("callDialogPosition");
    if (saved) {
      return JSON.parse(saved);
    }
    if (dialogPosition) {
      return dialogPosition;
    }
    return { x: 255, y: 100 };
  });

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });

  const dialogRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [minimizeTransform, setMinimizeTransform] = useState<string | null>(null);
  const minimizeTargetRef = useRef<string | null>(null);

  const cancelMinimize = useCallback(() => {
    minimizeTargetRef.current = null;
    setMinimizeTransform(null);
    setIsMinimizing(false);
  }, []);

  // Fly the card into the dock: measure where its bottom right corner sits relative to the dock's
  // corner, then hand the browser a frame at the identity transform before moving to the target.
  // Setting the target in the same commit that drops the dialog-enter class gives it nothing to
  // interpolate from and it jumps straight to the end state.
  const startMinimize = useCallback(() => {
    if (isMinimizing) {
      return;
    }
    const card = dialogRef.current;
    if (!card) {
      setIsMinimized(true);
      return;
    }
    const rect = card.getBoundingClientRect();
    const deltaX = window.innerWidth - DOCK_MARGIN - rect.right;
    const deltaY = window.innerHeight - DOCK_MARGIN - rect.bottom;
    minimizeTargetRef.current = `translate(${deltaX}px, ${deltaY}px) scale(0.12)`;
    setIsMinimizing(true);
  }, [isMinimizing]);

  useEffect(() => {
    if (!isMinimizing || minimizeTransform) {
      return;
    }
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (minimizeTargetRef.current) {
          setMinimizeTransform(minimizeTargetRef.current);
        }
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [isMinimizing, minimizeTransform]);

  useEffect(() => {
    if (!minimizeTransform) {
      return;
    }
    const timeoutId = setTimeout(() => {
      setIsMinimized(true);
      cancelMinimize();
    }, MINIMIZE_MS);
    return () => clearTimeout(timeoutId);
  }, [minimizeTransform, cancelMinimize]);

  const expandFromDock = useCallback(() => {
    cancelMinimize();
    setIsMinimized(false);
  }, [cancelMinimize]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  useEffect(() => {
    registerCloseHandler(handleClose);
    return () => registerCloseHandler(null);
  }, [registerCloseHandler, handleClose]);

  // A live call must stay visible, so clicking away parks it in the mini dock instead of closing.
  // An incoming call is exempt: hiding the answer controls out from under a ringing call is worse
  // than leaving the card up.
  const handleDismiss = useCallback(() => {
    if (isCallSurfaceActive) {
      startMinimize();
    } else {
      handleClose();
    }
  }, [isCallSurfaceActive, handleClose, startMinimize]);

  useDismissOnOutsideClick(dialogRef, handleDismiss, !isMinimized && !isClosing && !incomingCall);

  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      onClose();
    }
  }, [isClosing, onClose]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-draggable="false"]')) return;

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      const maxX = window.innerWidth - DEFAULT_WIDTH;
      const maxY = window.innerHeight - 200;

      const newPosition = {
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      };

      setPosition(newPosition);
      setDialogPosition(newPosition);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    registerDialerTabSwitch(() => setMainTab("dialer"));
  }, [registerDialerTabSwitch]);

  useEffect(() => {
    if (isCallSurfaceActive) {
      setMainTab("dialer");
    }
  }, [isCallSurfaceActive]);

  useEffect(() => {
    if (isMinimized && (incomingCall || (!activeCall && !heldCall))) {
      setIsMinimized(false);
    }
  }, [isMinimized, incomingCall, activeCall, heldCall]);

  // Dialing from Recent Calls is a click outside the card, so the dismissal starts closing it and
  // the button's own openCallDialog lands while it is still mounted. Any open request wins.
  useEffect(() => {
    setIsClosing(false);
    cancelMinimize();
    setIsMinimized(false);
  }, [openRequestId, cancelMinimize]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem("callDialogPosition", JSON.stringify(position));
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [position]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset, position]);

  return (
    <>
    {isMinimized && activeCall && <MiniCallDock onExpand={expandFromDock} />}
    <div
      ref={dialogRef}
      className={`fixed bg-background border rounded-lg shadow-2xl z-50 select-none flex flex-col overflow-hidden ${
        isClosing ? "dialog-exit" : isMinimizing ? "" : "dialog-enter"
      }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${DEFAULT_WIDTH}px`,
        maxHeight: `calc(100vh - ${position.y}px)`,
        cursor: isDragging ? "grabbing" : "default",
        display: isMinimized ? "none" : undefined,
        transform: isMinimizing ? (minimizeTransform ?? "translate(0px, 0px) scale(1)") : undefined,
        opacity: minimizeTransform ? 0 : undefined,
        transition: `transform ${MINIMIZE_MS}ms ease-in, opacity ${MINIMIZE_MS}ms ease-in`,
        transformOrigin: isMinimizing
          ? "bottom right"
          : triggerPosition
            ? `${triggerPosition.x - position.x}px ${triggerPosition.y - position.y}px`
            : "0% 50%",
      }}
      onAnimationEnd={handleAnimationEnd}
    >
      <div
        className="flex items-center justify-between px-4 py-3 border-b cursor-grab active:cursor-grabbing shrink-0 bg-background"
        onMouseDown={handleMouseDown}
      >
        <GripVertical className="h-5 w-5 text-muted-foreground" />
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="relative h-8 w-8 overflow-visible"
            onClick={() => {
              navigate("/inbox?channel=calls");
              if (incomingCall) {
                return;
              }
              if (activeCall) {
                startMinimize();
              } else {
                handleClose();
              }
            }}
            data-draggable="false"
            title={missedCalls > 0 ? `All calls (${missedCalls} missed)` : "All calls"}
          >
            <History className="h-4 w-4 dark:text-white" />
            {missedCalls > 0 && (
              <Badge className="absolute -right-1 -top-1 flex size-4 items-center justify-center p-0 text-[10px] tabular-nums">
                {missedCalls > 9 ? "9+" : missedCalls}
              </Badge>
            )}
          </Button>
          {isCallSurfaceActive && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={startMinimize}
              data-draggable="false"
              title="Minimize"
            >
              <Minus className="h-4 w-4 dark:text-white" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={isCallSurfaceActive ? startMinimize : handleClose}
            data-draggable="false"
            title={isCallSurfaceActive ? "Minimize" : "Close"}
          >
            <X className="h-4 w-4 dark:text-white" />
          </Button>
        </div>
      </div>

      <Tabs
        defaultValue="dialer"
        value={mainTab}
        onValueChange={setMainTab}
        className="flex-1 flex flex-col overflow-hidden"
        data-draggable="false"
      >
        {!isCallSurfaceActive && (
          <TabsList className="w-full grid grid-cols-2 rounded-none border-b shrink-0 bg-background">
            <TabsTrigger value="dialer">Dialer</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
        )}

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <TabsContent value="dialer" className="p-4 m-0" data-vaul-no-drag>
            <DialerTab
              answeredCall={activeCall}
              incomingCall={incomingCall}
              incomingCallEvent={incomingCallEvent}
            />
          </TabsContent>

          <TabsContent value="settings" className="p-4 m-0" data-vaul-no-drag>
            <SettingsTab />
          </TabsContent>
        </div>
      </Tabs>
    </div>
    </>
  );
}
